import { useState, useEffect, useCallback, useRef } from "react";
import { supabase } from "@/shared/integrations/supabase/client";
import {
  getAccessTokenForEdge,
  getSupabaseEdgeFunctionHeaders,
  getSupabaseFunctionsBaseUrl,
} from "@/shared/lib/supabaseFunctions";
import { useAuth } from "@/shared/hooks/useAuth";
import { toast } from "@/shared/hooks/use-toast";
import type { Database } from "@/shared/integrations/supabase/types";

// ─── Types ───────────────────────────────────────────────────────────────────

type DbMessage = Database["public"]["Tables"]["user_messages"]["Row"];
type DbThread = Database["public"]["Tables"]["user_message_threads"]["Row"];
type DbResumo = Database["public"]["Tables"]["correio_resumos_ia"]["Row"];

export interface UserMessage extends DbMessage {
  /** Enriched: nome do remetente (vem do sender_name na row) */
  sender_name: string;
  /** Enriched: contagem de anexos (lazy-loaded) */
  attachment_count?: number;
}

export interface MessageThread extends DbThread {
  last_message_preview?: string;
}

export type ResumoIA = DbResumo;

export type PastaCorreio = "entrada" | "enviadas" | "favoritas" | "lixeira" | "sistema" | "feedbacks";

// ─── Constants ───────────────────────────────────────────────────────────────

const NETWORK_ERROR_COOLDOWN_MS = 60_000;
const MAX_CONSECUTIVE_ERRORS = 3;
const NOTIFICATION_SOUND_FREQ = [880, 1100];

// ─── Online status hook ──────────────────────────────────────────────────────

function useOnlineStatus(): boolean {
  const [isOnline, setIsOnline] = useState(() =>
    typeof navigator !== "undefined" ? navigator.onLine : true
  );
  useEffect(() => {
    const on = () => setIsOnline(true);
    const off = () => setIsOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);
  return isOnline;
}

// ─── Notification helpers ────────────────────────────────────────────────────

function playNotificationSound() {
  try {
    const ctx = new AudioContext();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(NOTIFICATION_SOUND_FREQ[0], ctx.currentTime);
    osc.frequency.setValueAtTime(NOTIFICATION_SOUND_FREQ[1], ctx.currentTime + 0.1);
    gain.gain.setValueAtTime(0.3, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);
    osc.connect(gain).connect(ctx.destination);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.4);
  } catch {
    // AudioContext not available (e.g., autoplay policy)
  }
}

function showDesktopNotification(title: string, body: string) {
  if (!("Notification" in window)) return;
  if (Notification.permission === "granted") {
    new Notification(title, { body, icon: "/favicon.ico", tag: "ferrugini-correio" });
  } else if (Notification.permission !== "denied") {
    Notification.requestPermission().then((perm) => {
      if (perm === "granted") {
        new Notification(title, { body, icon: "/favicon.ico", tag: "ferrugini-correio" });
      }
    });
  }
}

// ─── Main hook ───────────────────────────────────────────────────────────────

export const useMessages = () => {
  const { user } = useAuth();
  const isOnline = useOnlineStatus();

  // State
  const [messages, setMessages] = useState<UserMessage[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [pastaAtiva, setPastaAtivaState] = useState<PastaCorreio>("entrada");
  const [messagesModuleAvailable, setMessagesModuleAvailable] = useState(true);
  const [hasGlowingIcon, setHasGlowingIcon] = useState(false);

  // Refs
  const consecutiveErrors = useRef(0);
  const networkCooldownUntil = useRef(0);
  const notificationPermissionRequested = useRef(false);

  // ─── Request desktop notification permission on mount ──────────────────
  useEffect(() => {
    if (notificationPermissionRequested.current) return;
    if ("Notification" in window && Notification.permission === "default") {
      notificationPermissionRequested.current = true;
      // Don't request immediately — wait for first user interaction
    }
  }, []);

  // ─── Error detection ──────────────────────────────────────────────────
  const isMissingTable = (message?: string | null) => {
    if (!message) return false;
    const n = message.toLowerCase();
    return n.includes("user_messages") && (n.includes("not found") || n.includes("does not exist") || n.includes("relation"));
  };

  const isNetworkError = (message?: string | null) => {
    if (!message) return false;
    const n = message.toLowerCase();
    return n.includes("failed to fetch") || n.includes("network") || n.includes("connection") || n.includes("err_connection") || n.includes("connection closed");
  };

  // ─── Fetch messages (by pasta) ────────────────────────────────────────
  const fetchMessages = useCallback(async (pasta?: PastaCorreio) => {
    if (!user || !messagesModuleAvailable) return;
    if (Date.now() < networkCooldownUntil.current) return;

    const targetPasta = pasta ?? pastaAtiva;

    try {
      let data: DbMessage[] | null = null;
      let error: { message: string } | null = null;

      switch (targetPasta) {
        case "entrada": {
          const res = await supabase
            .from("user_messages")
            .select("*")
            .eq("recipient_id", user.id)
            .is("deleted_at", null)
            .order("created_at", { ascending: false })
            .limit(50);
          data = res.data;
          error = res.error;
          break;
        }
        case "enviadas": {
          const res = await supabase.rpc("buscar_enviadas_correio");
          data = res.data;
          error = res.error;
          break;
        }
        case "favoritas": {
          const res = await supabase
            .from("user_messages")
            .select("*")
            .eq("recipient_id", user.id)
            .eq("favorita", true)
            .is("deleted_at", null)
            .order("created_at", { ascending: false })
            .limit(50);
          data = res.data;
          error = res.error;
          break;
        }
        case "lixeira": {
          const res = await supabase.rpc("buscar_lixeira_correio");
          data = res.data;
          error = res.error;
          break;
        }
        case "sistema": {
          const res = await supabase
            .from("user_messages")
            .select("*")
            .eq("recipient_id", user.id)
            .eq("message_type", "system")
            .is("deleted_at", null)
            .order("created_at", { ascending: false })
            .limit(50);
          data = res.data;
          error = res.error;
          break;
        }
        case "feedbacks": {
          const res = await supabase
            .from("user_messages")
            .select("*")
            .eq("recipient_id", user.id)
            .in("message_type", ["feedback_response", "feedback_recebido"])
            .is("deleted_at", null)
            .order("created_at", { ascending: false })
            .limit(50);
          data = res.data;
          error = res.error;
          break;
        }
      }

      if (error) {
        if (isMissingTable(error.message)) {
          setMessagesModuleAvailable(false);
          setMessages([]);
          setUnreadCount(0);
          setLoading(false);
          return;
        }
        if (isNetworkError(error.message)) {
          consecutiveErrors.current += 1;
          if (consecutiveErrors.current >= MAX_CONSECUTIVE_ERRORS) {
            networkCooldownUntil.current = Date.now() + NETWORK_ERROR_COOLDOWN_MS;
            consecutiveErrors.current = 0;
          }
        }
        setLoading(false);
        return;
      }

      consecutiveErrors.current = 0;
      const msgs = (data ?? []) as UserMessage[];
      setMessages(msgs);

      // Unread count is always from "entrada"
      if (targetPasta === "entrada") {
        const unread = msgs.filter((m) => !m.read).length;
        setUnreadCount(unread);
        setHasGlowingIcon(unread > 0);
      }

      setLoading(false);
    } catch {
      setLoading(false);
    }
  }, [user, messagesModuleAvailable, pastaAtiva]);

  // ─── Unread count for badge (always from entrada) ─────────────────────
  const fetchUnreadCount = useCallback(async () => {
    if (!user || !messagesModuleAvailable) return;
    const { count, error } = await supabase
      .from("user_messages")
      .select("id", { count: "exact", head: true })
      .eq("recipient_id", user.id)
      .eq("read", false)
      .is("deleted_at", null);
    if (!error && count !== null) {
      setUnreadCount(count);
      setHasGlowingIcon(count > 0);
    }
  }, [user, messagesModuleAvailable]);

  // ─── Initial fetch + refetch on pasta change ──────────────────────────
  useEffect(() => {
    void fetchMessages();
  }, [fetchMessages]);

  useEffect(() => {
    void fetchUnreadCount();
  }, [fetchUnreadCount]);

  // ─── Realtime subscription (stable — does NOT depend on pastaAtiva) ─────
  const fetchMsgsRef = useRef(fetchMessages);
  fetchMsgsRef.current = fetchMessages;
  const fetchUnreadRef = useRef(fetchUnreadCount);
  fetchUnreadRef.current = fetchUnreadCount;
  const pastaAtivaRef = useRef(pastaAtiva);
  pastaAtivaRef.current = pastaAtiva;

  useEffect(() => {
    if (!user || !messagesModuleAvailable || !isOnline) return;

    const handleNewMessage = (newMsg: UserMessage) => {
      if (pastaAtivaRef.current === "entrada") {
        setMessages((prev) => {
          if (prev.some((m) => m.id === newMsg.id)) return prev;
          return [newMsg, ...prev];
        });
      }
      setUnreadCount((prev) => prev + 1);
      setHasGlowingIcon(true);

      playNotificationSound();
      toast({
        title: "Nova mensagem",
        description: newMsg.subject || "Você recebeu uma nova mensagem",
      });
      showDesktopNotification(
        "Ferrugini — Nova mensagem",
        newMsg.subject || "Você recebeu uma nova mensagem"
      );
    };

    let subscribed = false;
    const channel = supabase
      .channel("user-messages-rt-" + user.id)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "user_messages",
          filter: `recipient_id=eq.${user.id}`,
        },
        (payload) => {
          handleNewMessage(payload.new as UserMessage);
        }
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "user_messages",
          filter: `recipient_id=eq.${user.id}`,
        },
        () => {
          void fetchMsgsRef.current();
          void fetchUnreadRef.current();
        }
      )
      .on(
        "postgres_changes",
        {
          event: "DELETE",
          schema: "public",
          table: "user_messages",
        },
        (payload) => {
          const deletedId = (payload.old as { id?: string })?.id;
          if (deletedId) {
            setMessages((prev) => {
              const msg = prev.find((m) => m.id === deletedId);
              if (msg && !msg.read) setUnreadCount((c) => Math.max(0, c - 1));
              return prev.filter((m) => m.id !== deletedId);
            });
          }
        }
      )
      .subscribe((status) => {
        if (status === "SUBSCRIBED") {
          if (subscribed) {
            void fetchMsgsRef.current();
            void fetchUnreadRef.current();
          }
          subscribed = true;
        }
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, messagesModuleAvailable, isOnline]);

  // ─── Actions ──────────────────────────────────────────────────────────

  const markAsRead = useCallback(async (id: string) => {
    if (!user || !messagesModuleAvailable) return;
    const readAt = new Date().toISOString();
    const { error } = await supabase
      .from("user_messages")
      .update({ read: true, read_at: readAt })
      .eq("id", id)
      .eq("recipient_id", user.id);
    if (error) return;
    let wasUnread = false;
    setMessages((prev) => {
      const msg = prev.find((m) => m.id === id);
      wasUnread = !!(msg && !msg.read);
      return prev.map((m) => (m.id === id ? { ...m, read: true, read_at: readAt } : m));
    });
    if (wasUnread) {
      setUnreadCount((prev) => {
        const next = Math.max(0, prev - 1);
        if (next === 0) setHasGlowingIcon(false);
        return next;
      });
    }
    void fetchUnreadCount();
  }, [user, messagesModuleAvailable, fetchUnreadCount]);

  const markAllAsRead = async () => {
    if (!user || !messagesModuleAvailable) return;
    const readAt = new Date().toISOString();
    const { error } = await supabase
      .from("user_messages")
      .update({ read: true, read_at: readAt })
      .eq("recipient_id", user.id)
      .eq("read", false);
    if (error) return;
    setMessages((prev) => prev.map((m) => ({ ...m, read: true, read_at: readAt })));
    setUnreadCount(0);
    setHasGlowingIcon(false);
  };

  const toggleFavorita = async (id: string) => {
    if (!messagesModuleAvailable) return;
    const { data, error } = await supabase.rpc("toggle_favorita_correio", { p_message_id: id });
    if (error) {
      toast({ title: "Erro ao favoritar", description: error.message, variant: "destructive" });
      return;
    }
    const novoEstado = data === true;
    setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, favorita: novoEstado } : m)));
  };

  const softDelete = async (id: string) => {
    if (!messagesModuleAvailable) return;
    const msg = messages.find((m) => m.id === id);
    // Feedback response: archive
    if (msg?.message_type === "feedback_response" && msg.reference_id) {
      await supabase.from("user_feedback").update({ status: "arquivado" }).eq("id", msg.reference_id).then(() => {}).catch(() => {});
    }
    const { error } = await supabase.rpc("soft_delete_mensagem", { p_message_id: id });
    if (error) {
      toast({ title: "Erro ao mover para lixeira", description: error.message, variant: "destructive" });
      return;
    }
    setMessages((prev) => {
      if (msg && !msg.read) setUnreadCount((c) => Math.max(0, c - 1));
      return prev.filter((m) => m.id !== id);
    });
    toast({ title: "Mensagem movida para a lixeira" });
  };

  const softDeleteBatch = async (ids: string[]) => {
    if (ids.length === 0 || !messagesModuleAvailable) return;
    const feedbackIds = messages
      .filter(
        (m) =>
          ids.includes(m.id) &&
          m.message_type === "feedback_response" &&
          m.reference_id
      )
      .map((m) => m.reference_id as string);
    if (feedbackIds.length > 0) {
      await supabase
        .from("user_feedback")
        .update({ status: "arquivado" })
        .in("id", feedbackIds)
        .then(() => {})
        .catch(() => {});
    }
    const { error } = await supabase.rpc("soft_delete_mensagens_batch", { p_ids: ids });
    if (error) {
      toast({ title: "Erro ao excluir mensagens", description: error.message, variant: "destructive" });
      return;
    }
    setMessages((prev) => {
      const deletedUnread = prev.filter((m) => ids.includes(m.id) && !m.read).length;
      setUnreadCount((c) => Math.max(0, c - deletedUnread));
      return prev.filter((m) => !ids.includes(m.id));
    });
    toast({ title: `${ids.length} mensagens movidas para a lixeira` });
  };

  const restaurar = async (id: string) => {
    const { error } = await supabase.rpc("restaurar_mensagem_correio", { p_message_id: id });
    if (error) {
      toast({ title: "Erro ao restaurar", description: error.message, variant: "destructive" });
      return;
    }
    setMessages((prev) => prev.filter((m) => m.id !== id));
    toast({ title: "Mensagem restaurada" });
  };

  const hardDelete = async (id: string) => {
    const { error } = await supabase.from("user_messages").delete().eq("id", id);
    if (error) {
      toast({ title: "Erro ao excluir permanentemente", description: error.message, variant: "destructive" });
      return;
    }
    setMessages((prev) => prev.filter((m) => m.id !== id));
  };

  const deleteAllMessages = async () => {
    if (!user || !messagesModuleAvailable) return;
    // Soft-delete all instead of hard-delete
    const ids = messages.map((m) => m.id);
    if (ids.length === 0) return;
    await softDeleteBatch(ids);
  };

  const encaminhar = async (params: {
    messageId: string;
    recipientIds: string[];
    senderName: string;
    notaEncaminhamento?: string;
  }) => {
    if (!messagesModuleAvailable) return { error: null };
    const original = messages.find((m) => m.id === params.messageId);
    if (!original) return { error: { message: "Mensagem original não encontrada" } };

    const fwdSubject = original.subject.startsWith("Enc:")
      ? original.subject
      : `Enc: ${original.subject}`;
    const fwdBody = params.notaEncaminhamento
      ? `${params.notaEncaminhamento}\n\n--- Mensagem encaminhada (${original.sender_name}) ---\n${original.message}`
      : `--- Mensagem encaminhada (${original.sender_name}) ---\n${original.message}`;

    const rows: Database["public"]["Tables"]["user_messages"]["Insert"][] = params.recipientIds.map((rid) => ({
      sender_id: user?.id ?? null,
      sender_name: params.senderName,
      recipient_id: rid,
      subject: fwdSubject,
      message: fwdBody,
      message_type: "direct",
      encaminhada_de: params.messageId,
      vinculo_tipo: original.vinculo_tipo,
      vinculo_id: original.vinculo_id,
      thread_id: original.thread_id,
    }));

    const { error } = await supabase.from("user_messages").insert(rows);
    return { error };
  };

  const sendMessage = async (params: {
    recipientIds: string[];
    subject: string;
    message: string;
    senderName: string;
    messageType?: string;
    referenceId?: string;
    vinculoTipo?: string;
    vinculoId?: string;
  }) => {
    if (!messagesModuleAvailable) return { error: null };
    const rows: Database["public"]["Tables"]["user_messages"]["Insert"][] = params.recipientIds.map((rid) => ({
      sender_id: user?.id ?? null,
      sender_name: params.senderName,
      recipient_id: rid,
      subject: params.subject,
      message: params.message,
      message_type: params.messageType || "direct",
      reference_id: params.referenceId || null,
      vinculo_tipo: params.vinculoTipo || null,
      vinculo_id: params.vinculoId || null,
    }));
    const { error } = await supabase.from("user_messages").insert(rows);
    return { error };
  };

  // ─── Thread operations ────────────────────────────────────────────────

  const concluirThread = async (threadId: string) => {
    const { data, error } = await supabase.rpc("concluir_thread_correio", { p_thread_id: threadId });
    if (error) {
      toast({ title: "Erro ao concluir thread", description: error.message, variant: "destructive" });
      return null;
    }
    const result = data as { sucesso?: boolean; erro?: string };
    if (result?.erro) {
      toast({ title: "Não foi possível concluir", description: result.erro, variant: "destructive" });
      return null;
    }
    return result;
  };

  const gerarResumoIA = async (threadId: string) => {
    const base = getSupabaseFunctionsBaseUrl();
    const token = await getAccessTokenForEdge();
    if (!base) {
      toast({ title: "Configuração inválida", description: "URL do Supabase não configurada.", variant: "destructive" });
      return null;
    }
    if (!token) {
      toast({ title: "Erro de autenticação", variant: "destructive" });
      return null;
    }
    const res = await fetch(`${base}/functions/v1/resumir-thread-correio`, {
      method: "POST",
      headers: {
        ...getSupabaseEdgeFunctionHeaders(token),
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ thread_id: threadId }),
    });
    let data: { sucesso?: boolean; error?: string; resumo_id?: string; titulo?: string; resumo?: string; decisoes?: string | null; proximos_passos?: string | null; modelo?: string };
    try {
      data = await res.json();
    } catch {
      toast({
        title: "Erro ao gerar resumo",
        description: "Resposta inválida do servidor",
        variant: "destructive",
      });
      return null;
    }
    if (!res.ok || !data.sucesso) {
      toast({
        title: "Erro ao gerar resumo",
        description: data.error || "Tente novamente",
        variant: "destructive",
      });
      return null;
    }
    return data as {
      resumo_id: string;
      titulo: string;
      resumo: string;
      decisoes: string | null;
      proximos_passos: string | null;
      modelo: string;
    };
  };

  const aprovarResumo = async (params: {
    resumoId: string;
    tituloFinal?: string;
    resumoFinal?: string;
    visivelExterno?: boolean;
  }) => {
    const { data, error } = await supabase.rpc("aprovar_resumo_ia", {
      p_resumo_id: params.resumoId,
      p_titulo_final: params.tituloFinal ?? null,
      p_resumo_final: params.resumoFinal ?? null,
      p_visivel_externo: params.visivelExterno ?? false,
    });
    if (error) {
      toast({ title: "Erro ao aprovar resumo", description: error.message, variant: "destructive" });
      return null;
    }
    const result = data as { sucesso?: boolean; erro?: string; andamento_id?: string };
    if (result?.erro) {
      toast({ title: "Erro", description: result.erro, variant: "destructive" });
      return null;
    }
    toast({ title: "Resumo aprovado e andamento registrado!" });
    return result;
  };

  const rejeitarResumo = async (resumoId: string) => {
    const { error } = await supabase.rpc("rejeitar_resumo_ia", { p_resumo_id: resumoId });
    if (error) {
      toast({ title: "Erro ao rejeitar", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Resumo rejeitado. Thread reaberta para nova conclusão." });
  };

  // ─── Fetch thread messages ────────────────────────────────────────────
  const fetchThreadMessages = async (threadId: string): Promise<UserMessage[]> => {
    const { data, error } = await supabase
      .from("user_messages")
      .select("*")
      .eq("thread_id", threadId)
      .order("created_at", { ascending: true })
      .limit(100);
    if (error) return [];
    return (data ?? []) as UserMessage[];
  };

  // ─── Fetch thread metadata ───────────────────────────────────────────
  const fetchThread = async (threadId: string): Promise<MessageThread | null> => {
    const { data, error } = await supabase
      .from("user_message_threads")
      .select("*")
      .eq("id", threadId)
      .maybeSingle();
    if (error || !data) return null;
    return data as MessageThread;
  };

  // ─── Return ───────────────────────────────────────────────────────────

  return {
    // State
    messages,
    unreadCount,
    loading,
    pastaAtiva,
    hasGlowingIcon,

    // Navigation
    setPastaAtiva: (pasta: PastaCorreio) => {
      setPastaAtivaState(pasta);
      setLoading(true);
      void fetchMessages(pasta);
    },

    // Basic actions
    markAsRead,
    markAllAsRead,
    toggleFavorita,
    sendMessage,
    encaminhar,

    // Delete actions (soft-delete = lixeira v2)
    softDelete,
    softDeleteBatch,
    restaurar,
    hardDelete,
    deleteAllMessages,

    /** @deprecated compat MessageInbox — use softDelete */
    deleteMessage: softDelete,
    /** @deprecated compat MessageInbox — use softDeleteBatch */
    deleteMessages: softDeleteBatch,

    // Thread actions
    concluirThread,
    gerarResumoIA,
    aprovarResumo,
    rejeitarResumo,
    fetchThreadMessages,
    fetchThread,

    // Refresh
    refetch: fetchMessages,
    refetchUnread: fetchUnreadCount,
  };
};
